import type { ChartDataPoint } from '../../types/ChartDataPoint';
import { isPointInZoomDomain } from '../../utils/scaleHelpers';
import type { XScaleType } from '../types/Scale';
import type { XDataKey } from '../types/XDataKey';

/**
 * Zoom domain type - varies by scale type:
 * - Continuous scales (time/linear): [min, max] as numbers
 * - Band scales: array of category strings
 * - null when not zoomed
 */
export type ZoomDomain = [number, number] | string[] | null;

// Re-export XScaleType for backwards compatibility
export type { XScaleType };

/**
 * Filters chart data to only include points within the zoom domain.
 *
 * This is the single source of truth for zoom domain filtering logic.
 * All components should use this function (or the computed `visibleData` from context)
 * instead of implementing their own filtering.
 *
 * Handles both continuous scales ([number, number]) and band scales (string[]).
 *
 * @param data - Array of chart data points
 * @param zoomDomain - Current zoom domain, or null if not zoomed
 * @param xScaleType - Type of x-axis scale
 * @param xDataKey - Key in data objects for x-axis values
 * @returns Filtered data array, or original array if not zoomed
 *
 * @example
 * ```tsx
 * // In a component
 * const visiblePoints = filterDataByZoomDomain(
 *   chartData,
 *   zoomDomain,
 *   xScaleType,
 *   xDataKey
 * );
 * ```
 */
export function filterDataByZoomDomain(
  data: ChartDataPoint[],
  zoomDomain: ZoomDomain,
  xScaleType: XScaleType,
  xDataKey: XDataKey,
): ChartDataPoint[] {
  // Not zoomed - return all data
  if (!zoomDomain) {
    return data;
  }

  return data.filter((datum) => {
    const xValue = datum[xDataKey];

    // Skip non-numeric values (null, undefined, strings for non-band scales)
    if (typeof xValue !== 'number') {
      return false;
    }

    return isPointInZoomDomain(xValue, xScaleType, zoomDomain);
  });
}
